import { slugify } from './slug.js';
import { sizeLabel } from './units.js';
/**
 * What to put on the grey line under a product's name.
 *
 * The list shows two things about an item: the generic — the everyday concept
 * a person shops for, "Havermelk" — and, under it, whichever one of those they
 * meant: "Oatly Barista". What it must never show is the shop's own product
 * title, "Oatly Barista Haverdrink Houdbaar 1L", which is a marketing string
 * with the answer buried in it.
 *
 * There is a worse version of that, and it is the one that actually shipped:
 * **selection names the provisional generic after the store's title** (see
 * `ai/classify-variant.ts`), so until the classifier reaches a variant, the
 * generic and the variant are the *same long string* and the tile rendered it
 * twice, once in black and once in grey.
 *
 * So there are two mechanisms and they answer different halves:
 *
 *   - `short_name` is the model's distilled label, written by the
 *     classification call that was already being made for this variant
 *     (AI.md §4). It is the good answer and it always wins.
 *   - Everything below is what runs *before* the model has reached the row,
 *     and when there is no model configured at all. It cannot invent
 *     "Havermelk", but it can drop what is already said on the line above and
 *     the size that is shown beside it, and it can decline to repeat a name
 *     verbatim — which is the whole of the doubled-name bug.
 *
 * Returning `null` means "there is nothing here worth a second line", which is
 * a normal answer: an any-store item and a manual entry have no variant at all.
 */
/** A token that is purely a pack size — "1L", "500g", "6", "2x200ml", "stuks". */
const SIZE_TOKEN = /^(\d+([.,]\d+)?\s*x\s*)?\d*([.,]\d+)?\s*(l|ml|cl|dl|kg|g|gr|mg|st|stk|stuk|stuks|pak|pack|blik|fles|bos|krat)?$/i;
/** Packaging words that describe the container rather than the product. */
const PACKAGING = new Set([
    'pak',
    'pack',
    'fles',
    'blik',
    'zak',
    'doos',
    'krat',
    'bos',
    'stuks',
    'stuk',
    'st',
    'houdbaar',
    'voordeelverpakking',
    'verpakking',
]);
const MAX = 64;
function tokensOf(text) {
    return new Set(text
        .split(/[\s/,+()]+/)
        .map((t) => slugify(t))
        .filter(Boolean));
}
export function variantLabel(input) {
    const generic = input.genericName?.trim() ?? '';
    const genericSlug = slugify(generic);
    const distinct = (candidate) => {
        const value = candidate?.trim().replace(/\s+/g, ' ') ?? '';
        if (!value)
            return null;
        // Never repeat the line above, however it was arrived at.
        if (genericSlug && slugify(value) === genericSlug)
            return null;
        return value.length > MAX ? `${value.slice(0, MAX - 1).trimEnd()}…` : value;
    };
    /*
     * The model's answer, when there is one. It is allowed to be anything —
     * including a brand the heuristics below would have thrown away.
     *
     * And when it *has* answered, that ends it either way: a label equal to the
     * generic is the model saying "nothing here distinguishes this one", so
     * falling through to derive something from the raw title would be second-
     * guessing the one component that actually read the product.
     */
    if (input.shortName?.trim())
        return distinct(input.shortName);
    const name = input.name.trim();
    if (!name)
        return null;
    /**
     * The brand, but only when it adds something the generic has not said.
     *
     * "AH" under "Halfvolle melk" is the useful half of a shop's own-brand
     * product. "Oatly" under "Oatly Barista Haverdrink 1L" is the same word
     * twice, which is the bug this whole module exists for — so a brand whose
     * words are all in the generic already is no answer at all.
     */
    const brandIfItAddsAnything = () => {
        const brand = input.brand?.trim();
        if (!brand)
            return null;
        const genericTokens = tokensOf(generic);
        const adds = [...tokensOf(brand)].some((token) => !genericTokens.has(token));
        return adds ? distinct(brand) : null;
    };
    /*
     * The doubled-name case, and the reason to check it before doing any word
     * surgery: a provisional generic *is* the product title, so dropping every
     * word the generic mentions leaves nothing at all. The brand is the only
     * thing that can still be worth saying here, and only on the terms above.
     */
    if (genericSlug && slugify(name) === genericSlug)
        return brandIfItAddsAnything();
    const noise = generic ? tokensOf(generic) : new Set();
    const size = sizeLabel(input.sizeValue, input.sizeUnit);
    // The size is already rendered beside the label, so it is noise here too.
    if (size)
        for (const token of tokensOf(size))
            noise.add(token);
    const kept = name
        .split(/\s+/)
        .filter((word) => {
        const slug = slugify(word);
        if (!slug)
            return false;
        if (noise.has(slug))
            return false;
        if (PACKAGING.has(slug))
            return false;
        return !SIZE_TOKEN.test(word);
    })
        .join(' ')
        // Separators orphaned by the words that went either side of them.
        .replace(/\s*[-–,/]\s*$/, '')
        .replace(/^\s*[-–,/]\s*/, '');
    // Nothing survived, so the brand is the only distinguishing thing left —
    // which is exactly right for "AH Biologisch Halfvolle Melk" under "Melk".
    return distinct(kept) ?? brandIfItAddsAnything();
}
//# sourceMappingURL=label.js.map